import { redirect } from "@tanstack/react-router";
import { buildHref } from "@softmax/web-addresses";
import { backendFetch } from "@softmax/observatory-client/transport";
import type { Schemas } from "@softmax/observatory-client/types";

import { readAccountProfile } from "@/features/account/records";
import { currentTosVersion } from "@/lib/tos";
import { getAuthSession, requireAuthSession } from "./session/session";
import {
  resolveRedirectUrl,
  type githubConnectSearchSchema,
  type returnSearchSchema,
  type signInSearchSchema,
} from "./identity-search";
import type { z } from "zod";
import { type ReadOptions } from "./identity-request";

export async function getSignIn({
  data,
  signal,
}: ReadOptions<z.infer<typeof signInSearchSchema>>) {
  const redirectUrl = resolveRedirectUrl(data.redirect_url);
  const session = await getAuthSession({ signal });

  if (session && !data.error) throw redirect({ href: redirectUrl });

  const providers = await backendFetch<
    Record<string, Schemas["WebsiteProviderResponse"]>
  >("", "/api/auth/providers", { signal: signal ?? null, cache: "no-store" });
  const errors = {
    oauth_failed: "Sign-in did not complete. Please try again.",
    account_in_use:
      "That provider account is already linked to another Softmax account.",
    account_not_linked:
      "Sign in with your existing provider, then link this provider from account settings.",
    session_changed: "Your account changed during sign-in. Please start again.",
  };
  return {
    redirectUrl,
    providers: Object.values(providers),
    error: data.error ? errors[data.error] : undefined,
  };
}

export async function getProfileCompletion({
  data,
  signal,
}: ReadOptions<z.infer<typeof returnSearchSchema>>) {
  const redirectUrl = resolveRedirectUrl(data.redirect_url);
  const session = await requireAuthSession({ returnHref: redirectUrl, signal });
  const { user, linkedAccounts } = await readAccountProfile({
    viewerId: session.user.id,
    signal,
  });

  if (user.profileCompleted && user.tosVersion === currentTosVersion) {
    throw redirect({ href: redirectUrl });
  }

  return {
    sessionUserId: session.user.id,
    callbackUrl: buildHref({
      name: "completeProfile",
      search: new URLSearchParams({ redirect_url: redirectUrl }),
    }),
    redirectUrl,
    discordEnabled: linkedAccounts.discordEnabled,
    discordUserId: linkedAccounts.discordUserId,
    user: {
      ...user,
      name: user.name ?? session.user.name ?? "",
      email: user.email ?? session.user.email ?? "",
      institution: user.institution ?? "",
    },
  };
}

export async function getGitHubConnection({
  data,
  signal,
}: ReadOptions<z.infer<typeof githubConnectSearchSchema>>) {
  const session = await requireAuthSession({
    returnHref: buildHref({
      name: "githubConnect",
      search: data.account
        ? new URLSearchParams({ account: data.account })
        : undefined,
    }),
    signal,
  });
  const { githubAuthorization } = await readAccountProfile({
    viewerId: session.user.id,
    signal,
  });
  return {
    sessionUserId: session.user.id,
    user: session.user,
    mismatch: Boolean(data.account && data.account !== session.user.id),
    connected: data.connected === "1" && githubAuthorization !== null,
    connection: githubAuthorization,
    error: data.error,
  };
}
